import React, { useState, useEffect } from "react";
import { FaCheckCircle, FaTimesCircle, FaPlus, FaUsers, FaTimes, FaUserTie } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import api from "../../services/api";
import "./admin.css";

const TaskManagement = () => {
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [teams, setTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Filters
  const [filterEmployee, setFilterEmployee] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTask, setNewTask] = useState({
    title: "",
    description: "",
    assigned_to: "",
    due_date: "",
  });
  const [taskFile, setTaskFile] = useState(null);
  // ✅ Assignment type: 'individual' or 'team'
  const [assignmentType, setAssignmentType] = useState('individual');
  const [selectedTeam, setSelectedTeam] = useState("");

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editForm, setEditForm] = useState({
    title: "",
    description: "",
    assigned_to: "",
    due_date: "",
  });
  const [editAssignmentType, setEditAssignmentType] = useState('individual');
  const [editSelectedTeam, setEditSelectedTeam] = useState("");

  // Submissions modal
  const [showSubmissionsModal, setShowSubmissionsModal] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [submissions, setSubmissions] = useState([]);

  const getToken = () => localStorage.getItem("token");

  // ✅ Get today's date
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // ✅ Validation helpers
  const validateTitle = (title) => {
    if (!title || title.trim().length === 0) return "Task title is required";
    if (title.trim().length < 3) return "Task title must be at least 3 characters";
    if (title.trim().length > 200) return "Task title must be less than 200 characters";
    if (/^[\d\s\W]+$/.test(title.trim())) return "Task title must contain meaningful text, not just numbers or symbols";
    return null;
  };

  const validateDescription = (description) => {
    if (description && description.trim().length > 1000) return "Description must be less than 1000 characters";
    return null;
  };

  const validateDueDate = (dueDate) => {
    if (dueDate && dueDate < getTodayDate()) return "Due date cannot be in the past";
    return null;
  };

  const validateFile = (file) => {
    if (!file) return null;
    const allowedTypes = [
      'application/pdf', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'image/jpeg', 'image/png', 'image/gif', 'text/plain', 'application/zip',
    ];
    const maxSize = 10 * 1024 * 1024;
    if (!allowedTypes.includes(file.type)) return "Invalid file type. Allowed: PDF, Word, Excel, Images, Text, ZIP";
    if (file.size > maxSize) return "File size must be less than 10MB";
    return null;
  };

  const fetchEmployees = async () => {
    try {
      const token = getToken();
      const res = await api.get("/admin/get_employees", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmployees(res.data.employees || []);
    } catch (err) {
      console.error(err);
    }
  };

  // ✅ Fetch teams
  const fetchTeams = async () => {
    try {
      const token = getToken();
      const res = await api.get("/admin/teams", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTeams(res.data.teams || []);
    } catch (err) {
      console.error("Failed to fetch teams:", err);
    }
  };

  const fetchTasks = async () => {
    try {
      setIsLoading(true);
      const token = getToken();
      const params = {};
      if (filterEmployee !== "all") params.assigned_to = filterEmployee;
      if (filterStatus !== "all") params.status = filterStatus;

      const res = await api.get("/admin/tasks", {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setTasks(res.data.tasks || []);
    } catch (err) {
      setErrorMsg(err.response?.data?.message || "Failed to load tasks");
    } finally {
      setIsLoading(false);
    }
  };

  // ---- CREATE ----
  const handleCreateTask = async (e) => {
  e.preventDefault();
  
  // ✅ Validate title
  const titleError = validateTitle(newTask.title);
  if (titleError) { setErrorMsg(titleError); setTimeout(() => setErrorMsg(""), 4000); return; }

  const descError = validateDescription(newTask.description);
  if (descError) { setErrorMsg(descError); setTimeout(() => setErrorMsg(""), 4000); return; }

  // ✅ Validate assignment selection
  if (assignmentType === 'individual' && !newTask.assigned_to) {
    setErrorMsg("Please select an employee");
    setTimeout(() => setErrorMsg(""), 4000);
    return;
  }
  if (assignmentType === 'team' && !selectedTeam) {
    setErrorMsg("Please select a team");
    setTimeout(() => setErrorMsg(""), 4000);
    return;
  }

  const dateError = validateDueDate(newTask.due_date);
  if (dateError) { setErrorMsg(dateError); setTimeout(() => setErrorMsg(""), 4000); return; }

  const fileError = validateFile(taskFile);
  if (fileError) { setErrorMsg(fileError); setTimeout(() => setErrorMsg(""), 4000); return; }
  
  try {
    setIsLoading(true);
    const token = getToken();
    const formData = new FormData();
    
    // Always append title
    formData.append("title", newTask.title.trim());
    
    // Only append description if it has a value
    if (newTask.description) {
      formData.append("description", newTask.description.trim());
    }
    
    // ✅ Send assignment type and appropriate ID (No empty strings appended)
    if (assignmentType === 'team') {
      formData.append("team_id", selectedTeam);
      formData.append("assignment_type", "team");
    } else {
      formData.append("assigned_to", newTask.assigned_to);
      formData.append("assignment_type", "individual");
    }
    
    // Only append due_date if it has a value
    if (newTask.due_date) {
      formData.append("due_date", newTask.due_date);
    }
    
    // Append file if it exists
    if (taskFile) formData.append("file", taskFile);

    await api.post("/admin/tasks/create", formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
    });
    
    setSuccessMsg("Task created successfully!");
    setShowCreateModal(false);
    resetCreateForm();
    fetchTasks();
    setTimeout(() => setSuccessMsg(""), 3000);
    
  } catch (err) {
    setErrorMsg(err.response?.data?.message || "Failed to create task");
    setTimeout(() => setErrorMsg(""), 4000);
  } finally {
    setIsLoading(false);
  }
};

  const resetCreateForm = () => {
    setNewTask({ title: "", description: "", assigned_to: "", due_date: "" });
    setTaskFile(null);
    setAssignmentType('individual');
    setSelectedTeam("");
  };

  // ---- EDIT ----
  const openEditModal = (task) => {
    setEditingTask(task);
    setEditForm({
      title: task.title,
      description: task.description || "",
      assigned_to: task.assigned_to || "",
      due_date: task.due_date || "",
    });
    // ✅ Set assignment type based on task data
    if (task.assignment_type === 'team' || task.team_id) {
      setEditAssignmentType('team');
      setEditSelectedTeam(task.team_id || "");
    } else {
      setEditAssignmentType('individual');
      setEditSelectedTeam("");
    }
    setShowEditModal(true);
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    
    const titleError = validateTitle(editForm.title);
    if (titleError) { setErrorMsg(titleError); setTimeout(() => setErrorMsg(""), 4000); return; }

    const descError = validateDescription(editForm.description);
    if (descError) { setErrorMsg(descError); setTimeout(() => setErrorMsg(""), 4000); return; }

    // ✅ Validate assignment selection
    if (editAssignmentType === 'individual' && !editForm.assigned_to) {
      setErrorMsg("Please select an employee");
      setTimeout(() => setErrorMsg(""), 4000);
      return;
    }
    if (editAssignmentType === 'team' && !editSelectedTeam) {
      setErrorMsg("Please select a team");
      setTimeout(() => setErrorMsg(""), 4000);
      return;
    }

    const dateError = validateDueDate(editForm.due_date);
    if (dateError) { setErrorMsg(dateError); setTimeout(() => setErrorMsg(""), 4000); return; }
    
    try {
      setIsLoading(true);
      const token = getToken();
      
      const payload = {
        title: editForm.title.trim(),
        description: editForm.description.trim(),
        due_date: editForm.due_date,
        assignment_type: editAssignmentType,
      };

      // ✅ Send appropriate ID based on assignment type
      if (editAssignmentType === 'team') {
        payload.assigned_to = "";
        payload.team_id = editSelectedTeam;
      } else {
        payload.assigned_to = editForm.assigned_to;
        payload.team_id = "";
      }

      await api.put(`/admin/tasks/update/${editingTask.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccessMsg("Task updated successfully!");
      setShowEditModal(false);
      fetchTasks();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setErrorMsg(err.response?.data?.message || "Update failed");
      setTimeout(() => setErrorMsg(""), 4000);
    } finally {
      setIsLoading(false);
    }
  };

  // ---- DELETE ----
  const handleDeleteTask = async (taskId, taskTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${taskTitle}"?`)) return;
    try {
      const token = getToken();
      await api.delete(`/admin/tasks/delete/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccessMsg("Task deleted successfully!");
      fetchTasks();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setErrorMsg(err.response?.data?.message || "Delete failed");
      setTimeout(() => setErrorMsg(""), 4000);
    }
  };

  // ---- Submissions ----
  const handleViewSubmissions = async (task) => {
    setCurrentTask(task);
    try {
      const token = getToken();
      const res = await api.get(`/admin/tasks/submissions?task_id=${task.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubmissions(res.data.submissions || []);
      setShowSubmissionsModal(true);
    } catch (err) {
      setErrorMsg("Failed to load submissions");
    }
  };

  const handleReview = async (submissionId, status) => {
    try {
      const token = getToken();
      await api.put("/admin/tasks/submissions/review", { submission_id: submissionId, status }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (currentTask) handleViewSubmissions(currentTask);
      fetchTasks();
      setSuccessMsg(`Submission ${status}!`);
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setErrorMsg(err.response?.data?.message || "Review failed");
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchTeams();
    fetchTasks();
  }, [filterEmployee, filterStatus]);

  // ✅ Get assigned display name
  const getAssignedDisplay = (task) => {
    if (task.assignment_type === 'team' && task.team_name) {
      return `${task.team_name}`;
    }
    return task.assigned_to_name || "—";
  };

  return (
    <div className="admin-container">
      <div className="header-banner mb-4">
        <div className="header-content">
          <div>
            <h1 className="header-title">Task Management </h1>
            <p className="header-subtitle">Assign, edit, delete and review tasks</p>
          </div>
          <button className="btn-add-student" onClick={() => { resetCreateForm(); setShowCreateModal(true); }}>
            <span className="btn-icon"><FaPlus /></span> Assign Task
          </button>
        </div>
      </div>

      {successMsg && <div className="success-message"><FaCheckCircle style={{ marginRight: "6px" }} />{successMsg}</div>}
      {errorMsg && <div className="error-message"><FaTimesCircle style={{ marginRight: "6px" }} />{errorMsg}</div>}

      <div className="students-card">
        <div className="card-header-custom">
          <div>
            <h3 className="card-title">All Tasks</h3>
            <p className="card-subtitle">{tasks.length} tasks</p>
          </div>
          <div className="header-actions">
            <select className="form-input filter-select" value={filterEmployee} onChange={(e) => setFilterEmployee(e.target.value)}>
              <option value="all">All Members</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>{emp.employeeName}</option>
              ))}
            </select>
            <select className="form-input filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        <div className="table-wrapper">
          <table className="students-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Assigned To</th>
                <th>Status</th>
                <th>Due</th>
                <th>Admin File</th>
                <th>Submission File</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan="7" className="text-center py-4">Loading...</td></tr>
              ) : tasks.length === 0 ? (
                <tr><td colSpan="7" className="empty-state">No tasks found</td></tr>
              ) : tasks.map((task) => (
                <tr key={task.id}>
                  <td>
                    <strong>{task.title}</strong>
                    {task.description && <p className="small text-muted mb-0">{task.description}</p>}
                    {task.assignment_type === 'team' && (
                      <span className="badge bg-info ms-1" style={{fontSize: '10px'}}>Team</span>
                    )}
                  </td>
                  <td>{getAssignedDisplay(task)}</td>
                  <td>
                    <span className={`status-badge status-${task.status}`}>{task.status}</span>
                  </td>
                  <td>{task.due_date || "—"}</td>
                  <td>
                    {task.file_path ? (
                      <a href={`https://dt-attendance-system.desired-techs.com/dt-backend/${task.file_path}`}
                         target="_blank" rel="noreferrer" className="btn-action btn-view" download>
                        Download
                      </a>
                    ) : "—"}
                  </td>
                  <td>
                    {task.submission_file_path ? (
                      <a href={`https://dt-attendance-system.desired-techs.com/dt-backend/${task.submission_file_path}`}
                         target="_blank" rel="noreferrer" className="btn-action btn-view" download>
                        Download
                      </a>
                    ) : "—"}
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      <button className="btn-action btn-edit" onClick={() => openEditModal(task)}> Edit</button>
                      <button className="btn-action btn-delete" onClick={() => handleDeleteTask(task.id, task.title)}> Delete</button>
                      <button className="btn-action btn-view" onClick={() => handleViewSubmissions(task)}>View</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* CREATE MODAL */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-custom">
              <div>
                <h3 className="modal-title">Assign New Task</h3>
                <p className="modal-subtitle">Create a task for an employee/intern or team</p>
              </div>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}><FaTimes /></button>
            </div>
            <div className="modal-body-custom">
              <form onSubmit={handleCreateTask}>
                <div className="form-group">
                  <label className="form-label">Task Title <span style={{color: 'red'}}>*</span></label>
                  <input type="text" className="form-input" value={newTask.title} 
                    onChange={(e) => setNewTask({...newTask, title: e.target.value})} 
                    placeholder="Enter a meaningful task title (min 3 characters)" maxLength={200} required />
                  <small className="text-muted">Min 3 characters, must contain meaningful text</small>
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea className="form-input" rows="3" style={{resize: 'none'}} value={newTask.description} 
                    onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                    placeholder="Optional task description (max 1000 characters)" maxLength={1000}></textarea>
                </div>

                {/* ✅ Assignment Type Checkboxes */}
                <div className="form-group">
                  <label className="form-label">Assign To <span style={{color: 'red'}}>*</span></label>
                  <div style={{ display: 'flex', gap: '20px', marginBottom: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: '500' }}>
                      <input 
                        type="checkbox" 
                        checked={assignmentType === 'individual'} 
                        onChange={() => { setAssignmentType('individual'); setSelectedTeam(""); }}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                       <FaUserTie style={{ marginRight: "6px" }} /> Individual Employee
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: '500' }}>
                      <input 
                        type="checkbox" 
                        checked={assignmentType === 'team'} 
                        onChange={() => { setAssignmentType('team'); setNewTask({...newTask, assigned_to: ""}); }}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                       <FaUsers style={{ marginRight: "6px" }} /> Team
                    </label>
                  </div>

                  {/* Individual Employee Select */}
                  {assignmentType === 'individual' && (
                    <select className="form-input" value={newTask.assigned_to} 
                      onChange={(e) => setNewTask({...newTask, assigned_to: e.target.value})} required>
                      <option value="">Select employee</option>
                      {employees.map((emp) => (
                        <option key={emp.id} value={emp.id}>{emp.employeeName} ({emp.role})</option>
                      ))}
                    </select>
                  )}

                  {/* Team Select */}
                  {assignmentType === 'team' && (
                    <select className="form-input" value={selectedTeam} 
                      onChange={(e) => setSelectedTeam(e.target.value)} required>
                      <option value="">Select team</option>
                      {teams.map((team) => (
                        <option key={team.id} value={team.id}>{team.name} ({team.member_count || team.members?.length || 0} members)</option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Due Date (optional)</label>
                  <input type="date" className="form-input" value={newTask.due_date} min={getTodayDate()}
                    onChange={(e) => setNewTask({...newTask, due_date: e.target.value})} />
                  <small className="text-muted">Only today and future dates can be selected</small>
                </div>
                <div className="form-group">
                  <label className="form-label">Attach File (optional)</label>
                  <input type="file" className="form-input" onChange={(e) => setTaskFile(e.target.files[0])}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt,.zip" />
                  <small className="text-muted">Allowed: PDF, Word, Excel, Images, Text, ZIP (max 10MB)</small>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn-cancel" onClick={() => setShowCreateModal(false)}>Cancel</button>
                  <button type="submit" className="btn-submit" disabled={isLoading}>{isLoading ? "Creating..." : "Create Task"}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {showEditModal && editingTask && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-custom">
              <div>
                <h3 className="modal-title">Edit Task</h3>
                <p className="modal-subtitle">Update task details</p>
              </div>
              <button className="modal-close" onClick={() => setShowEditModal(false)} aria-label="Close edit task modal"><FaTimes /></button>
            </div>
            <div className="modal-body-custom">
              <form onSubmit={handleUpdateTask}>
                <div className="form-group">
                  <label className="form-label">Task Title <span style={{color: 'red'}}>*</span></label>
                  <input type="text" className="form-input" value={editForm.title} 
                    onChange={(e) => setEditForm({...editForm, title: e.target.value})} 
                    placeholder="Enter a meaningful task title (min 3 characters)" maxLength={200} required />
                  <small className="text-muted">Min 3 characters, must contain meaningful text</small>
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea style={{resize: "none"}} className="form-input" rows="3" value={editForm.description} 
                    onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                    placeholder="Optional task description (max 1000 characters)" maxLength={1000}></textarea>
                </div>

                {/* ✅ Edit Assignment Type Checkboxes */}
                <div className="form-group">
                  <label className="form-label">Assign To <span style={{color: 'red'}}>*</span></label>
                  <div style={{ display: 'flex', gap: '20px', marginBottom: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: '500' }}>
                      <input type="checkbox" checked={editAssignmentType === 'individual'} 
                        onChange={() => { setEditAssignmentType('individual'); setEditSelectedTeam(""); }}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                      Individual Employee
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: '500' }}>
                      <input type="checkbox" checked={editAssignmentType === 'team'} 
                        onChange={() => { setEditAssignmentType('team'); setEditForm({...editForm, assigned_to: ""}); }}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                      Team
                    </label>
                  </div>

                  {editAssignmentType === 'individual' && (
                    <select className="form-input" value={editForm.assigned_to} 
                      onChange={(e) => setEditForm({...editForm, assigned_to: e.target.value})} required>
                      <option value="">Select employee</option>
                      {employees.map((emp) => (
                        <option key={emp.id} value={emp.id}>{emp.employeeName} ({emp.role})</option>
                      ))}
                    </select>
                  )}

                  {editAssignmentType === 'team' && (
                    <select className="form-input" value={editSelectedTeam} 
                      onChange={(e) => setEditSelectedTeam(e.target.value)} required>
                      <option value="">Select team</option>
                      {teams.map((team) => (
                        <option key={team.id} value={team.id}>{team.name} ({team.member_count || team.members?.length || 0} members)</option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Due Date (optional)</label>
                  <input type="date" className="form-input" value={editForm.due_date} min={getTodayDate()}
                    onChange={(e) => setEditForm({...editForm, due_date: e.target.value})} />
                  <small className="text-muted">Only today and future dates can be selected</small>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn-cancel" onClick={() => setShowEditModal(false)}>Cancel</button>
                  <button type="submit" className="btn-submit" disabled={isLoading}>{isLoading ? "Updating..." : "Update Task"}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* SUBMISSIONS MODAL */}
      {showSubmissionsModal && currentTask && (
        <div className="modal-overlay" onClick={() => setShowSubmissionsModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "600px" }}>
            <div className="modal-header-custom">
              <div>
                <h3 className="modal-title">Submissions for: {currentTask.title}</h3>
                <p className="modal-subtitle">Assigned to: {getAssignedDisplay(currentTask)}</p>
              </div>
              <button className="modal-close" onClick={() => setShowSubmissionsModal(false)} aria-label="Close task submissions modal"><FaTimes /></button>
            </div>
            <div className="modal-body-custom">
              {submissions.length === 0 ? (
                <p>No submissions yet.</p>
              ) : (
                <table className="students-table">
                  <thead>
                    <tr><th>Employee</th><th>Comment</th><th>File</th><th>Status</th><th>Action</th></tr>
                  </thead>
                  <tbody>
                    {submissions.map((sub) => (
                      <tr key={sub.id}>
                        <td>{sub.employeeName}</td>
                        <td>{sub.comment || "—"}</td>
                        <td>{sub.file_path ? <a href={`https://dt-attendance-system.desired-techs.com/dt-backend/${sub.file_path}`} target="_blank" rel="noreferrer">View File</a> : "—"}</td>
                        <td><span className={`status-badge status-${sub.status}`}>{sub.status}</span></td>
                        <td>
                          {sub.status === 'submitted' && (
                            <div className="d-flex gap-2">
                              <button className="btn-action btn-checkin" onClick={() => handleReview(sub.id, 'approved')}>Approve</button>
                              <button className="btn-action btn-checkout" onClick={() => handleReview(sub.id, 'rejected')}>Reject</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskManagement;